"use client";

import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Search, User, LogOut, Building2, Heart } from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import { cn } from "@/lib/utils";
import { openCommandPalette } from "@/components/common/command-palette";
import { useUser } from "@/lib/user-context";
import { logout } from "@/lib/auth-api";
import { mainNavItems, matchRoute } from "@/lib/navigation";

export function SiteHeader() {
  const pathname = usePathname();
  const router = useRouter();
  const { me, refresh } = useUser();
  const [menuOpen, setMenuOpen] = useState(false);
  const [lastPath, setLastPath] = useState(pathname);
  const menuRef = useRef<HTMLDivElement>(null);

  // 경로 변경 감지 → 메뉴 닫기 (렌더 중 setState, React 19 권장 패턴)
  if (lastPath !== pathname) {
    setLastPath(pathname);
    if (menuOpen) setMenuOpen(false);
  }

  // Escape 키로 드롭다운 닫기
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && menuOpen) setMenuOpen(false);
    },
    [menuOpen],
  );

  useEffect(() => {
    if (menuOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [menuOpen, handleKeyDown]);

  // admin 페이지에서는 사이트 헤더 숨김
  if (pathname.startsWith("/admin")) return null;

  async function handleLogout(withOAuth = false) {
    try {
      const res = await logout();
      if (withOAuth && res.kakao_logout_url) {
        window.location.href = res.kakao_logout_url;
        return;
      }
    } catch {}
    await refresh();
    setMenuOpen(false);
    router.push("/wiki");
    router.refresh();
  }

  return (
    <header className="sticky top-0 z-50 border-b border-black/6 bg-white/85 backdrop-blur-xl transition-colors">
      <div className="mx-auto flex h-14 max-w-4xl items-center justify-between px-5">
        {/* 로고 */}
        <Link href="/wiki" className="flex items-center transition-opacity hover:opacity-80">
          <span
            className="text-xl font-semibold tracking-[-0.3px] text-gray-900"
            style={{ fontFamily: "var(--font-logo)" }}
          >
            skinagent
          </span>
        </Link>

        {/* PC 네비게이션 (lg+) */}
        <nav aria-label="메인 메뉴" className="hidden items-center gap-7 lg:flex">
          {mainNavItems
            .filter((item) => !item.mobileOnly)
            .map((item) => (
              <Link
                key={item.href}
                href={item.href}
                aria-current={matchRoute(pathname, item) ? "page" : undefined}
                className={cn(
                  "text-sm font-medium transition-colors",
                  matchRoute(pathname, item)
                    ? "text-primary font-semibold"
                    : "text-muted-foreground hover:text-primary",
                )}
              >
                {item.label}
              </Link>
            ))}
        </nav>

        {/* 우측 액션 */}
        <div className="flex items-center gap-1">
          <button
            onClick={() => openCommandPalette()}
            className="text-muted-foreground hover:bg-muted hover:text-foreground flex h-9 w-9 items-center justify-center rounded-full transition-colors"
            aria-label="검색"
          >
            <Search className="h-[18px] w-[18px]" strokeWidth={2} aria-hidden="true" />
          </button>

          {/* 인증 상태 */}
          {me?.user ? (
            <div className="relative">
              <button
                onClick={() => setMenuOpen((v) => !v)}
                aria-expanded={menuOpen}
                aria-haspopup="true"
                aria-label="사용자 메뉴"
                className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-50 text-violet-700 transition-colors hover:bg-violet-100"
              >
                <User className="h-[18px] w-[18px]" strokeWidth={2} />
              </button>

              {menuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
                  <div
                    ref={menuRef}
                    role="menu"
                    aria-label="사용자 메뉴"
                    className="absolute top-11 right-0 z-50 w-56 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-xl"
                  >
                    <div className="border-b border-gray-100 px-4 py-3">
                      <div className="truncate text-sm font-bold text-gray-900">{me.user.name}</div>
                      <div className="text-muted-foreground truncate text-[10px]">
                        {me.user.email?.includes("@oauth.") ? "" : me.user.email}
                      </div>
                      {me.hospital_admin && (
                        <span className="mt-1 inline-block rounded-full bg-violet-50 px-2 py-0.5 text-[9px] font-bold text-violet-700">
                          ✓ 인증된 병원 관리자
                        </span>
                      )}
                    </div>

                    <Link
                      href="/account"
                      role="menuitem"
                      onClick={() => setMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <Heart className="h-3.5 w-3.5" />
                      마이 페이지
                    </Link>

                    {me.hospital_admin && (
                      <Link
                        href="/hospital/dashboard"
                        role="menuitem"
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                      >
                        <Building2 className="h-3.5 w-3.5" />
                        병원 대시보드
                      </Link>
                    )}

                    <button
                      role="menuitem"
                      onClick={() => handleLogout(me.oauth_provider === "kakao")}
                      className="flex w-full items-center gap-2 border-t border-gray-100 px-4 py-2.5 text-sm text-gray-500 hover:bg-gray-50"
                    >
                      <LogOut className="h-3.5 w-3.5" />
                      로그아웃
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <>
              <Link
                href="/signup"
                className="gradient-cta hidden items-center rounded-full px-4 py-2 text-sm font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 sm:inline-flex"
              >
                회원가입
              </Link>
              <Link
                href="/signin"
                className="rounded-full bg-violet-600 px-4 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-violet-700"
              >
                로그인
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
